import {useTranslation} from "react-i18next";
import {css, Theme} from "@emotion/react";
import {calculateRemSize, flex, p, pv} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";
import {differenceInMonths, isAfter, isBefore, parseISO} from "date-fns";

interface IConstructionProgressBarProps {
    startDate: string;
    endDate: string;
    formatDate: (dateString: string, format: string) => string;
}

export const ConstructionProgressBar = ({startDate, endDate, formatDate}: IConstructionProgressBarProps) => {
    const {t} = useTranslation();
    const calculateProgress = (): number => {
        const now = new Date();
        const start = parseISO(startDate);
        const end = parseISO(endDate);

        // If construction hasn't started yet
        if (isBefore(now, start)) {
            return 0;
        }

        // If construction is finished
        if (isAfter(now, end)) {
            return 100;
        }

        // Calculate progress based on months
        const totalMonths = differenceInMonths(end, start);
        const monthsElapsed = differenceInMonths(now, start);

        if (totalMonths <= 0) {
            return 100;
        }

        const progress = (monthsElapsed / totalMonths) * 100;
        return Math.min(Math.max(Math.round(progress), 0), 100);
    };

    const progress = calculateProgress();

    return (
        <>
            <div css={progressWrapper}>
                <div css={progressBar}>
                    <div css={progressFill(progress)} />
                </div>
                <div css={progressPercentage}>{progress}%</div>
            </div>

            <div css={progressLabels}>
                <Text variant="info_txt_1">{formatDate(startDate, "Q 'kw.' yyyy")}</Text>
                <Text variant="info_txt_1" strong>
                    {t("offer_box.construction_today")}
                </Text>
                <Text variant="info_txt_1">{formatDate(endDate, "Q 'kw.' yyyy")}</Text>
            </div>
        </>
    );
};

const progressWrapper = css`
    ${flex("center", "flex-start")};
    gap: ${calculateRemSize(1)};
    ${p(1, 0, 2)};
    position: relative;
`;

const progressBar = css`
    flex: 1;
    height: ${calculateRemSize(1.5)};
    background-color: rgba(215, 175, 225, 0.4);
    border-radius: ${calculateRemSize(1)};
    position: relative;
    overflow: visible;
`;

const progressFill = (percentage: number) => css`
    height: 100%;
    width: ${percentage}%;
    background-color: #d7afe1;
    border-radius: ${calculateRemSize(1)};
    transition: width 0.3s ease;
`;

const progressPercentage = css`
    font-size: ${calculateRemSize(1.5)};
    font-weight: 600;
    color: #333;
    min-width: ${calculateRemSize(4)};
    text-align: right;
`;

const progressLabels = css`
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(1)};
    color: #666;
`;
